import React from 'react'
import PropTypes from 'prop-types'

export interface CheckboxProps {
  handleSelect?: React.MouseEventHandler<HTMLButtonElement>
  children?: React.ReactNode
  attributes?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & { 'data-testid': string }
}

export interface HighlightableProps {
  children?: React.ReactNode
}

export type CheckboxComponent = React.FC<CheckboxProps>
export type HighlightableComponent = React.FC<HighlightableProps>

export interface FocusableProps {
  isActiveItem: (props: { id: string }) => boolean
  isInActiveItemHierarchy: (props: { id: string }) => boolean
  itemId: string
  clearFocus: () => void
  expand?: () => void
  children: (props: { Checkbox: CheckboxComponent, Highlightable: HighlightableComponent }) => React.ReactNode
}

interface FocusableState {
  wasLastActiveItem: boolean
}

export default class Focusable extends React.Component<FocusableProps, FocusableState> {
  private readonly highlightableRef = React.createRef<HTMLDivElement>()
  private readonly checkboxRef = React.createRef<HTMLButtonElement>()
  static propTypes: any = {}

  constructor (props: FocusableProps) {
    super(props)

    this.state = {
      wasLastActiveItem: false
    }
  }

  componentDidMount = (): void => {
    this.handleActiveCode()
  }

  componentDidUpdate = (): void => {
    this.handleActiveCode()
  }

  handleActiveCode = (): void => {
    const { itemId, isActiveItem, isInActiveItemHierarchy, expand } = this.props

    if (this.state.wasLastActiveItem && isActiveItem({ id: itemId })) { return }

    if (isActiveItem({ id: itemId })) {
      this.checkboxRef.current?.focus()
      this.scrollToElement()
      this.setState({
        wasLastActiveItem: true
      })
    } else if (this.state.wasLastActiveItem) {
      this.setState({
        wasLastActiveItem: false
      })
    }

    if ((expand != null) && isInActiveItemHierarchy({ id: itemId })) {
      expand()
    }
  }

  scrollToElement = (): void => {
    if (!this.isInViewport()) {
      window.scroll({ top: this.highlightableRef.current?.getBoundingClientRect().top })
    }
  }

  isInViewport = (): boolean => {
    const rect = this.highlightableRef.current?.getBoundingClientRect()

    return (
      rect !== undefined &&
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight ?? document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth ?? document.documentElement.clientWidth)
    )
  }

  activeClass = (): string => {
    return this.props.isActiveItem({ id: this.props.itemId }) ? 'active-code' : ''
  }

  renderCheckbox: CheckboxComponent = ({ handleSelect, attributes, children }) => {
    return (
      <button
        ref={this.checkboxRef}
        onClick={handleSelect}
        role='checkbox'
        onBlur={() => {
          this.props.clearFocus()
        }}
        {...attributes}
      >
        {children}
      </button>
    )
  }

  renderHighlightable: HighlightableComponent = ({ children }) => {
    return (
      <div ref={this.highlightableRef} className={this.activeClass()}>
        {children}
      </div>
    )
  }

  render (): React.ReactNode {
    return (
      <>
        {this.props.children({ Checkbox: this.renderCheckbox, Highlightable: this.renderHighlightable })}
      </>
    )
  }
}

Focusable.propTypes = {
  isActiveItem: PropTypes.func.isRequired,
  isInActiveItemHierarchy: PropTypes.func.isRequired,
  itemId: PropTypes.string.isRequired,
  clearFocus: PropTypes.func.isRequired,
  expand: PropTypes.func
}
